import React, { useEffect, useState, useRef, useCallback } from "react";
import { Check, Trash2, Search } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getCostServices,
  updateCostService,
  clearError,
  createCostService,
  deleteCostService,
} from "../../store/slices/costServicesSlice";
import { getPartners } from "../../store/slices/partnersSlice";
import { withVersion } from "../../utils/concurrency";
import {
  CreateCostService,
  CostService,
  UpdateCostService,
  CostServiceFilters as Filters,
} from "../../types/costService";
import Button from "../../components/UI/Button";
import { Modal } from "../../components/UI/Modal";
import { CostServiceForm } from "../../components/costServices/CostServiceForm";
import { CostServiceTable } from "../../components/costServices/CostServicesTable";
import { Pagination } from "../../components/ListView/Pagination";
import { Input } from "../../components/UI/FormField";
import { PartnerSearchDropdown } from "../../components/services/PartnerSearchDropdown";

export const CostServicesListing: React.FC = () => {
  const { t } = useTranslation(["costservices", "common"]);
  const dispatch = useAppDispatch();
  const {
    costServices,
    loading,
    error,
    pagination: { total, page, pages },
  } = useAppSelector((state) => state.costServices);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<CostService | null>(
    null,
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>({
    search: "",
    page: 1,
    limit: 20,
    partnerId: undefined,
  });
  const [modalServiceType, setModalServiceType] = useState<string | null>(null);

  const prevSearch = useRef(filters.search);

  useEffect(() => {
    const searchChanged = prevSearch.current !== filters.search;
    prevSearch.current = filters.search;

    const params = {
      search: filters.search,
      page: filters.page,
      limit: filters.limit,
      ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
    };

    if (searchChanged) {
      if (filters.search === "") {
        dispatch(getCostServices(params));
      } else {
        const timer = setTimeout(() => dispatch(getCostServices(params)), 750);
        return () => clearTimeout(timer);
      }
      return;
    }
    dispatch(getCostServices(params));
  }, [dispatch, filters]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleFiltersChange = (newFilters: Partial<Filters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page || 1,
    }));
    setSelectedIds([]);
  };

  const handlePageChange = (page: number) => handleFiltersChange({ page });

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? costServices.map((s) => s._id) : []);
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((sid) => sid !== id),
    );
  };

  const handleCreateService = async (data: CreateCostService) => {
    try {
      await dispatch(createCostService(data)).unwrap();
      toast.success(t("costservices:messages.createdSuccess"));
      setIsModalOpen(false);
    } catch (error) {}
  };

  const handleUpdateService = async (data: Partial<CostService>) => {
    if (!editingService) return;

    try {
      if (data.type === "MATRIX") {
        if (!data.zones || data.zones.length === 0) {
          toast.error(t("costservices:form.matrixRequiresZone"));
          return;
        }
        if (!data.bands || data.bands.length === 0) {
          toast.error(t("costservices:form.matrixRequiresBand"));
          return;
        }
        if (!data.priceMatrix || data.priceMatrix.length === 0) {
          toast.error(t("costservices:form.matrixRequiresPrices"));
          return;
        }

        const serviceInfo: UpdateCostService = {
          name: data.name,
          type: data.type,
          description: data.description,
          tax: data.tax,
          partnerId: data.partnerId,
          zones: data.zones,
          bands: data.bands,
          priceMatrix: data.priceMatrix,
        };

        await dispatch(
          updateCostService({
            id: editingService._id,
            serviceData: withVersion(serviceInfo, editingService),
          }),
        ).unwrap();
      } else {
        if (!data.name || !data.type) {
          toast.error(t("costservices:form.serviceCodeAndTypeRequired"));
          return;
        }

        const serviceInfo: UpdateCostService = {
          name: data.name,
          type: data.type,
          description: data.description,
          price: data.price ?? undefined,
          tax: data.tax,
          partnerId: data.partnerId,
        };

        await dispatch(
          updateCostService({
            id: editingService._id,
            serviceData: withVersion(serviceInfo, editingService),
          }),
        ).unwrap();
      }

      toast.success(t("costservices:messages.updatedSuccess"));
      setIsModalOpen(false);
      setEditingService(null);
    } catch (error) {
      console.error(error);
      toast.error(
        typeof error === "string"
          ? error
          : t("costservices:messages.updateFailed"),
      );
    }
  };

  const handleEdit = (service: CostService) => {
    setEditingService(service);
    setModalServiceType(service.type);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t("costservices:page.deleteConfirm"))) {
      try {
        await dispatch(deleteCostService(id)).unwrap();
        toast.success(t("costservices:messages.deletedSuccess"));
        setSelectedIds((prev) => prev.filter((sid) => sid !== id));
      } catch (error) {}
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingService(null);
    setModalServiceType(null);
  };

  const pagination = {
    page,
    pages,
    total,
    hasNext: page < pages,
    hasPrev: page > 1,
  };

  const fetchPartnersForForm = useCallback(
    async (params: { search: string; page: number; limit: number }) => {
      const result = await dispatch(
        getPartners({
          search: params.search,
          page: params.page,
          limit: params.limit,
        }),
      ).unwrap();
      return result;
    },
    [dispatch],
  );

  // Handler for partner filter dropdown
  const handlePartnerFilterChange = (partnerId: string | null) => {
    handleFiltersChange({
      partnerId: partnerId === "GENERAL" ? undefined : partnerId || undefined,
    });
  };

  return (
    <div>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-dark-900 mb-4">
            {t("costservices:page.title")}
          </h1>
          <div className="flex items-center justify-between gap-4 max-sm:flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-[250px] max-w-[250px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    type="text"
                    placeholder={t("costservices:page.search")}
                    value={filters.search}
                    onChange={(e) =>
                      handleFiltersChange({ search: e.target.value })
                    }
                    className="pl-10 bg-white w-[220px]"
                  />
                </div>
              </div>

              <div className="w-[180px] max-w-[250px]">
                <PartnerSearchDropdown
                  value={filters.partnerId || null}
                  onChange={handlePartnerFilterChange}
                  fetchPartners={fetchPartnersForForm}
                  placeholder={t("costservices:page.allPartners")}
                  showGeneralOption={true}
                  generalOptionLabel={t("costservices:page.generalNoPartner")}
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button onClick={() => setIsModalOpen(true)} variant="primary">
                {t("costservices:page.addService")}
              </Button>
            </div>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-blue-600" />
              <span className="text-blue-800 font-medium">
                {t("costservices:page.itemsSelected", {
                  count: selectedIds.length,
                })}
              </span>
            </div>
            <button className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
              <Trash2 className="h-4 w-4" />
              <span>{t("costservices:page.deleteSelected")}</span>
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <CostServiceTable
            services={costServices}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            onEdit={handleEdit}
            onDelete={handleDelete}
            loading={loading}
          />
          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            pageSize={filters.limit}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={
            editingService
              ? `${t("costservices:page.idPrefix")}${editingService.costServiceId}`
              : t("costservices:page.addNewService")
          }
          size={modalServiceType === "MATRIX" ? "xl" : "lg"}
        >
          <div className="flex-1 overflow-y-auto">
            <CostServiceForm
              service={editingService || undefined}
              onSubmit={(data) => {
                if (editingService) {
                  return handleUpdateService(data as any);
                } else {
                  return handleCreateService(data as CreateCostService);
                }
              }}
              loading={loading}
              isEditMode={!!editingService}
              onTypeChange={(type) => setModalServiceType(type)}
              fetchPartners={fetchPartnersForForm}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};
